import { Link } from "react-router-dom";
import { Seo } from "../lib/seo";
import {
  MktButton,
  MktCtaBand,
  MktFaq,
  MktFaqItem,
  MktHero,
  MktPlan,
  MktSection,
} from "../components/marketing";
import {
  PRICING_PLANS,
  PRICING_GRID_NOTE,
  BALDER_USAGE_EXPLANATION,
  VAT_INCLUDED_LABEL,
} from "../lib/pricing-config";

/**
 * PUBLIC pricing page (odinforge.io/pricing) — rendered inside the marketing
 * shell (MarketingLayout) so it matches the rest of the site.
 *
 * TRUTHFUL + HUMANE PRICING: prices are the approved, VAT-inclusive, clean numbers
 * (€0 / €20 / €50 / From €150) sourced from a central config (`lib/pricing-config`),
 * never invented or hard-coded per card. Core and Pro are purchasable directly;
 * Dedicated stays contact-based because its final price depends on infrastructure,
 * migration, onboarding and support. BALDER AI usage is described as LIMITED — each
 * plan carries a monthly allowance and additional usage always requires approval.
 * No "Most Popular" badge, no countdown, no fake scarcity.
 */
export function Pricing(): JSX.Element {
  return (
    <>
      <Seo
        title="Pricing — start free, upgrade when you need more"
        description="ODIN's Free plan is €0. Core is €20/month, Pro €50/month and Dedicated from €150/month — all prices include VAT. Every plan includes a limited BALDER AI allowance; additional AI usage requires your approval. Cancel anytime."
        path="/pricing"
      />
      <MktHero
        kicker="Pricing"
        title="Simple, honest pricing"
        subhead="Start free and upgrade only when you need more. Every plan lists what it includes and its main limits, so you can choose calmly — no pressure, no hidden terms, and cancellation is always straightforward."
      >
        <MktButton to="/login" variant="primary">
          Start free
        </MktButton>
        <MktButton href="#pricing-faq" variant="secondary">
          Pricing questions
        </MktButton>
      </MktHero>

      <MktSection
        kicker="Plans"
        title="Pick the plan that fits your community"
        lead="Free covers a whole server with ODIN's all-in-one bot. Core and Pro have clear monthly prices you can choose directly; Dedicated is quoted to fit your setup. All prices include VAT, and every plan includes a limited BALDER AI allowance."
      >
        <div className="mkt-plans">
          {PRICING_PLANS.map((plan) => (
            <MktPlan
              key={plan.code}
              name={plan.name}
              price={plan.price}
              cadence={plan.cadence}
              priceNote={plan.vatIncluded ? VAT_INCLUDED_LABEL : undefined}
              usageNote={plan.balderDisclosure}
              featured={plan.featured}
              desc={plan.desc}
              features={plan.features}
              cta={
                <MktButton to={plan.ctaTo} variant={plan.ctaVariant}>
                  {plan.ctaLabel}
                </MktButton>
              }
            />
          ))}
        </div>

        <p className="mkt-pricing-vatnote">{PRICING_GRID_NOTE}</p>

        <div className="mkt-pricing-notes">
          <p>{BALDER_USAGE_EXPLANATION}</p>
          <p>
            You can cancel at any time. Paid plans stay active until the end of the period you paid for and
            then return to Free — nothing in your Discord server is deleted automatically.
          </p>
        </div>
      </MktSection>

      <MktSection
        kicker="Questions"
        title="Pricing, answered plainly"
        lead="The things people ask before picking a plan."
        id="pricing-faq"
      >
        <MktFaq>
          <MktFaqItem question="Is the Free plan really free?" defaultOpen>
            <p>
              Yes — €0, with no trial timer and no credit card. It covers one server with ODIN's all-in-one bot, with
              full design and safe dry-run review. You're never pushed onto a paid plan.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Is VAT included?">
            <p>Yes. The prices displayed on the ODIN pricing page include VAT.</p>
          </MktFaqItem>
          <MktFaqItem question="Is BALDER AI unlimited?">
            <p>
              No. Each plan includes a defined amount of BALDER AI usage. You can view your usage in the ODIN
              dashboard. Additional usage can be purchased separately and will not be charged without your
              approval.
            </p>
          </MktFaqItem>
          <MktFaqItem question="What happens when I reach my BALDER limit?">
            <p>
              ODIN will notify you before the allowance is exhausted. You can purchase additional usage or
              move to a plan with a higher allowance. Other ODIN functionality will continue operating
              normally.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Are there unexpected usage charges?">
            <p>
              No. ODIN displays usage information and requires approval before applying additional usage
              charges.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Why does Dedicated say “Contact us”?">
            <p>
              Core and Pro have clear monthly prices you can choose directly. Dedicated's final price can
              depend on infrastructure, migration, onboarding and support requirements, so we set it up with
              you rather than show a fixed number.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Can I cancel anytime?">
            <p>
              Yes. Cancellation is straightforward, your plan stays active until the end of the period you
              paid for, and then it simply returns to Free. Your Discord server and its data stay yours throughout.
            </p>
          </MktFaqItem>
          <MktFaqItem question="Do you make uptime promises?">
            <p>
              No. ODIN is rolling out carefully and we make no uptime promises — see the{" "}
              <Link to="/status">status page</Link> for the platform's current state.
            </p>
          </MktFaqItem>
        </MktFaq>
      </MktSection>

      <MktCtaBand
        kicker="Free plan · €0"
        title="Start free — upgrade only if you outgrow it"
        lead="Create an account, connect your Discord server, and design it with full dry-run review. No credit card, cancel anytime."
        note="Free to start · No credit card · No coding needed · Cancel anytime"
      >
        <MktButton to="/login" variant="primary">
          Start free
        </MktButton>
        <MktButton to="/contact" variant="secondary">
          Talk to us
        </MktButton>
      </MktCtaBand>
    </>
  );
}
